import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { PasswordAccountRow, UserRole } from "@march/shared";
import { apiGet, apiPost, errorText } from "../lib/api.ts";
import { OutlineButton } from "./FilterBar.tsx";
import { theme } from "../theme.ts";
import type { Lang } from "../lib/i18n.tsx";

const SITE = location.origin;
const roleOptions = (long: boolean) => (
  <>
    <option value="factory">{long ? "Factory: Home and Forecast only" : "Factory"}</option>
    <option value="viewer">{long ? "Viewer: every page, read only" : "Viewer"}</option>
    <option value="editor">Editor</option>
    <option value="admin">Admin</option>
  </>
);
// Blank = they haven't picked one; the site opens in English.
const langOptions = (
  <>
    <option value="en">English</option>
    <option value="zh">简体中文</option>
    <option value="zh-TW">繁體中文</option>
  </>
);
const fmt = (t: number | null) => (t ? new Date(t).toLocaleString() : "—");

/**
 * Email + password sign-in for people without a March Microsoft account (the factory). An admin creates
 * the login and is shown a one-time password to pass on; the person picks their own at first sign-in.
 * Reset gives a new one-time password and signs them out everywhere; Disable signs them out at once.
 * Language is the site language they get on their next visit; they can still switch it themselves.
 */
export function PasswordLoginsCard() {
  const qc = useQueryClient();
  const list = useQuery<{ accounts: PasswordAccountRow[] }>({
    queryKey: ["password-accounts"],
    queryFn: () => apiGet("/api/admin/password-accounts"),
  });
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<UserRole>("factory");
  const [lang, setLang] = useState<Lang>("zh");
  const [shown, setShown] = useState<{ email: string; password: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["password-accounts"] });
    qc.invalidateQueries({ queryKey: ["audit"] });
  };
  const run = async (fn: () => Promise<void>) => {
    setErr(null);
    try {
      await fn();
      refresh();
    } catch (e) {
      setErr(errorText(e));
    }
  };
  const create = () =>
    run(async () => {
      setShown(await apiPost<{ email: string; password: string }>("/api/admin/password-accounts", { email, name, role, lang }));
      setEmail("");
      setName("");
    });
  const reset = (a: PasswordAccountRow) =>
    confirm(`Give ${a.email} a new one-time password? They'll be signed out everywhere.`) &&
    run(async () => setShown(await apiPost(`/api/admin/password-accounts/${encodeURIComponent(a.email)}/reset`)));
  const disable = (a: PasswordAccountRow) =>
    confirm(`Disable ${a.email}? They'll be signed out at once.`) &&
    run(() => apiPost(`/api/admin/password-accounts/${encodeURIComponent(a.email)}/disable`));
  const changeRole = (a: PasswordAccountRow, r: UserRole) =>
    run(() => apiPost(`/api/admin/password-accounts/${encodeURIComponent(a.email)}/role`, { role: r }));
  const changeLang = (a: PasswordAccountRow, l: Lang) =>
    run(() => apiPost(`/api/admin/password-accounts/${encodeURIComponent(a.email)}/lang`, { lang: l }));
  const enable = (a: PasswordAccountRow) => run(() => apiPost(`/api/admin/password-accounts/${encodeURIComponent(a.email)}/enable`));

  const muted = { color: theme.color.textMuted, fontSize: theme.font.size.sm };
  const input = { padding: "6px 10px", border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.md };
  const code = { fontFamily: theme.font.mono, background: theme.color.bg, padding: "2px 6px", borderRadius: theme.radius.sm, fontSize: theme.font.size.md };
  const th = { textAlign: "left" as const, padding: "6px 8px", ...muted, fontWeight: theme.font.weight.semibold };
  const td = { padding: "6px 8px", borderTop: `1px solid ${theme.color.border}`, fontSize: theme.font.size.sm };
  const accounts = list.data?.accounts ?? [];

  return (
    <div style={{ display: "grid", gap: theme.space.lg }}>
      <div style={muted}>
        For people without a March Microsoft account, such as the factory. They sign in at {SITE} with email and password
        instead of Microsoft. Staff with a March account should keep using Microsoft.
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" type="email" style={{ ...input, minWidth: 220 }} />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" style={{ ...input, minWidth: 160 }} />
        <select
          value={role}
          onChange={(e) => {
            const r = e.target.value as UserRole;
            setRole(r);
            setLang(r === "factory" ? "zh" : "en");
          }}
          style={input}
        >
          {roleOptions(true)}
        </select>
        <select value={lang} onChange={(e) => setLang(e.target.value as Lang)} style={input} aria-label="Language">
          {langOptions}
        </select>
        <OutlineButton onClick={() => void create()} disabled={!email.includes("@")}>
          Create login
        </OutlineButton>
        {err && <span style={{ color: theme.color.danger, fontSize: theme.font.size.sm }}>{err}</span>}
      </div>

      {shown && (
        <div style={{ background: theme.color.warningFaint, padding: 12, borderRadius: theme.radius.md, display: "grid", gap: 6 }}>
          <div>
            <b>One-time password for {shown.email}</b>: shown only now. Pass it on privately; they choose their own
            password when they first sign in.
          </div>
          <span style={code}>{shown.password}</span>
          <div>
            <OutlineButton onClick={() => void navigator.clipboard.writeText(shown.password)}>Copy</OutlineButton>{" "}
            <OutlineButton onClick={() => setShown(null)}>Done</OutlineButton>
          </div>
        </div>
      )}

      {accounts.length > 0 && (
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th style={th}>Email</th>
              <th style={th}>Role</th>
              <th style={th}>Language</th>
              <th style={th}>Status</th>
              <th style={th}>Last signed in</th>
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => {
              const locked = a.locked_until != null && a.locked_until > Date.now();
              return (
                <tr key={a.email} style={{ opacity: a.disabled_at ? 0.5 : 1 }}>
                  <td style={td}>
                    {a.email}
                    {a.display_name && <span style={muted}> · {a.display_name}</span>}
                  </td>
                  <td style={td}>
                    <select value={a.role} onChange={(e) => void changeRole(a, e.target.value as UserRole)} style={{ ...input, padding: "3px 6px" }}>
                      {roleOptions(false)}
                    </select>
                  </td>
                  <td style={td}>
                    <select value={a.lang ?? "en"} onChange={(e) => void changeLang(a, e.target.value as Lang)} style={{ ...input, padding: "3px 6px" }} aria-label={`Language for ${a.email}`}>
                      {langOptions}
                    </select>
                  </td>
                  <td style={td}>
                    {a.disabled_at
                      ? `Disabled ${fmt(a.disabled_at)}`
                      : locked
                        ? "Locked (wrong passwords) for a few minutes"
                        : a.must_change
                          ? "Waiting for first sign-in"
                          : "Active"}
                  </td>
                  <td style={td}>{a.must_change && !a.disabled_at ? "—" : fmt(a.last_login_at)}</td>
                  <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                    {a.disabled_at ? (
                      <OutlineButton onClick={() => void enable(a)}>Enable</OutlineButton>
                    ) : (
                      <>
                        <OutlineButton onClick={() => void reset(a)}>Reset password</OutlineButton>{" "}
                        <OutlineButton onClick={() => void disable(a)}>Disable</OutlineButton>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
